<template>
  <div class="logo-wrapper">
    <svg
        viewBox="-3 0 340 58"
        class="logo"
    >
      <!-- Letters: S, K, O, D, D, S inside the 1.975x scaled group -->
      <g transform="matrix(1.9755407,0,0,1.9755407,-2.23467,-4.1881463)">
        <g
            v-for="(letter, index) in letters"
            :key="index"
            class="letter"
            :class="{ visible: lettersVisible }"
            :style="letterStyle(index)"
        >
          <path :d="letter" fill="#ffffff"/>
        </g>
      </g>

      <!-- A is a SIBLING (not inside the 1.975x group) — uses raw path coords -->
      <!-- Rendered last so it paints on top -->
      <g
          class="letter-a-group"
          :class="{
          'a-enter': phase === 'a-enter',
          'a-settled': phase === 'a-settled' || phase === 'letters-in' || phase === 'done' || phase === 'letters-out',
          'a-exit': phase === 'a-exit',
          'a-hidden': phase === 'idle'
        }"
      >
        <path
            d="m 2.801361,56.004394 c -1.071495,-0.38291 -1.702283,-0.95456 -2.22267103,-2.01428 -0.487023,-0.99178 -0.496882,-1.07283 -0.488675,-4.01741 0.0097,-3.48607 0.409532,-6.0568 1.76390103,-11.34153 1.865569,-7.27942 4.671719,-14.5822 8.582177,-22.33443 3.588279,-7.1135203 5.386351,-10.0223703 7.488214,-12.1141103 1.523344,-1.51602 2.673812,-2.19387 4.307314,-2.53786 4.520352,-0.95191994 7.427435,1.29092 11.773685,9.0835103 6.473039,11.6058 11.371983,24.03276 13.484235,34.20495 0.773702,3.726 0.891523,6.53336 0.337624,8.04468 -0.510238,1.39219 -1.28406,2.33231 -2.348348,2.85301 -0.843426,0.41265 -1.096942,0.45303 -2.398874,0.38208 -2.957838,-0.16118 -3.932394,-1.32539 -4.987411,-5.95799 -0.95591,-4.19741 -1.891753,-6.25754 -4.091525,-9.00692 -2.554204,-3.19236 -6.778978,-5.86769 -11.375068,-7.20323 -2.237616,-0.65021 -4.432193,-0.95283 -6.915706,-0.95362 l -2.225852,-7.1e-4 -0.55443,1.81196 c -1.274949,4.16673 -2.310647,9.29156 -3.0277357,14.98182 -0.3835043,3.04319 -0.5453953,3.64312 -1.2713123,4.71116 -0.765381,1.1261 -1.975997,1.65481 -3.766808,1.64505 -0.783034,-0.004 -1.711265,-0.11053 -2.062734,-0.23613 z m 30.425908,-25.48233 c 0,-0.26694 -2.261658,-6.01627 -3.257192,-8.28007 -1.988707,-4.52222 -5.769496,-11.51002 -6.157572,-11.38066 -0.262356,0.0874 -3.054802,4.60423 -4.546543,7.35403 -1.019676,1.87962 -3.362809,6.75227 -3.362809,6.99311 0,0.0288 0.974482,0.0964 2.165515,0.15 4.856246,0.21878 10.072142,1.90127 13.479223,4.34798 1.117545,0.80254 1.679378,1.0754 1.679378,0.81561 z"
            fill="#ff6f45"
        />
      </g>
    </svg>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted } from 'vue'

const props = defineProps<{ isLoaded: boolean }>()
const emit = defineEmits<{ (e: 'animation-complete'): void }>()

type Phase = 'idle' | 'a-enter' | 'a-settled' | 'letters-in' | 'done' | 'letters-out' | 'a-exit'

const phase = ref<Phase>('idle')
const lettersVisible = ref(false)
const LETTERS_COUNT = 6

const letterStyle = (index: number) => {
  if (lettersVisible.value) {
    return { transitionDelay: `${index * 120}ms` }
  } else {
    return { transitionDelay: `${(LETTERS_COUNT - 1 - index) * 80}ms` }
  }
}

let animationTimer: ReturnType<typeof setTimeout> | null = null
const clearTimer = () => { if (animationTimer) { clearTimeout(animationTimer); animationTimer = null } }
const after = (ms: number, fn: () => void) => { animationTimer = setTimeout(fn, ms) }

const pendingReverse = ref(false)

const playForward = () => {
  clearTimer()
  phase.value = 'a-enter'
  lettersVisible.value = false
  after(600, () => {
    phase.value = 'a-settled'
    after(500, () => {
      phase.value = 'letters-in'
      lettersVisible.value = true
      after(LETTERS_COUNT * 120 + 400, () => {
        phase.value = 'done'
        if (pendingReverse.value) {
          pendingReverse.value = false
          playReverse()
        }
      })
    })
  })
}

const playReverse = () => {
  clearTimer()
  phase.value = 'letters-out'
  lettersVisible.value = false
  after(LETTERS_COUNT * 80 + 400, () => {
    phase.value = 'a-exit'
    after(700, () => {
      phase.value = 'idle'
      emit('animation-complete')
    })
  })
}

// Play forward automatically on mount — no external trigger needed
onMounted(() => {
  playForward()
})

// When isLoaded becomes true, reverse as soon as the intro reaches 'done'.
// If it arrives mid-animation, set a flag so playForward's final step picks it up.
watch(() => props.isLoaded, (loaded) => {
  if (!loaded) return
  if (phase.value === 'done') {
    playReverse()
  } else {
    // Intro still running — reverse will fire automatically once 'done' is reached
    pendingReverse.value = true
  }
})

const letters: string[] = [
  "m 37.68,30.56 h -7.56 c -1.32,0 -2.36,-0.68 -2.36,-2.16 0,-1.48 1.04,-2.16 2.36,-2.16 h 7.2 c 1.92,0 3.44,-0.44 3.44,-2.6 0,-4.12 -12.6,0.24 -12.6,-8.12 0,-3.64 2.4,-5.28 6.8,-5.28 h 6.72 c 1.28,0 2.32,0.68 2.32,2.16 0,1.48 -1.04,2.16 -2.32,2.16 h -7 c -0.72,0 -1.72,0.04 -1.72,1 0,2.76 12.6,-0.32 12.6,8.12 0,4.84 -3.36,6.88 -7.88,6.88 z",
  "m 54,19.6 v 8.48 c 0,1.44 -0.76,2.6 -2.4,2.6 -1.64,0 -2.4,-1.16 -2.4,-2.6 V 4.72 c 0,-1.44 0.76,-2.6 2.4,-2.6 1.64,0 2.4,1.16 2.4,2.6 V 15.6 c 3.08,-0.28 5.16,-1.8 6.36,-4.04 0.6,-1.08 1.48,-1.56 2.32,-1.56 1.36,0 2.16,0.8 2.16,1.96 0,2.4 -2.56,4.76 -5.28,5.56 v 0.08 c 5.68,2.04 6.64,7.4 6.64,10.44 0,1.32 -0.52,2.64 -2.4,2.64 -1.64,0 -2.48,-1.08 -2.4,-3.6 C 61.48,24.8 59.36,19.92 54,19.6 Z",
  "m 106.32,16.8 c 0,8.96 -6.16,14 -13,14 -6.84,0 -13,-5.04 -13,-14 0,-8.96 6.16,-14 13,-14 6.84,0 13,5.04 13,14 z m -5.68,0 c 0,-5.28 -2.96,-9.44 -7.32,-9.44 -4.36,0 -7.32,4.16 -7.32,9.44 0,5.28 2.96,9.44 7.32,9.44 4.36,0 7.32,-4.16 7.32,-9.44 z",
  "m 127.48,4.72 v 23.72 c 0,1.24 -0.52,2.12 -2.4,2.12 h -6.2 c -6.64,0 -9.92,-4.08 -9.92,-8.88 0,-6 4.52,-11.68 13.72,-11.68 V 4.72 c 0,-1.44 0.76,-2.6 2.4,-2.6 1.64,0 2.4,1.16 2.4,2.6 z m -4.8,21.52 V 14.32 c -4.32,0 -8.68,2.88 -8.68,7.32 0,2.52 1.6,4.6 4.32,4.6 z",
  "m 149.72,4.72 v 23.72 c 0,1.24 -0.52,2.12 -2.4,2.12 h -6.2 c -6.64,0 -9.92,-4.08 -9.92,-8.88 0,-6 4.52,-11.68 13.72,-11.68 V 4.72 c 0,-1.44 0.76,-2.6 2.4,-2.6 1.64,0 2.4,1.16 2.4,2.6 z m -4.8,21.52 V 14.32 c -4.32,0 -8.68,2.88 -8.68,7.32 0,2.52 1.6,4.6 4.32,4.6 z",
  "m 163.24,30.56 h -7.56 c -1.32,0 -2.36,-0.68 -2.36,-2.16 0,-1.48 1.04,-2.16 2.36,-2.16 h 7.2 c 1.92,0 3.44,-0.44 3.44,-2.6 0,-4.12 -12.6,0.24 -12.6,-8.12 0,-3.64 2.4,-5.28 6.8,-5.28 h 6.72 c 1.28,0 2.32,0.68 2.32,2.16 0,1.48 -1.04,2.16 -2.32,2.16 h -7 c -0.72,0 -1.72,0.04 -1.72,1 0,2.76 12.6,-0.32 12.6,8.12 0,4.84 -3.36,6.88 -7.88,6.88 z"
]
</script>

<style scoped>
.logo-wrapper {
  position: fixed;
  inset: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  background: #000;
  z-index: 9999;
}

.logo {
  width: auto;
  height: 40%;
  max-width: 85vw;
}

/* ─── Letter opacity ─── */
.letter {
  opacity: 0;
  transition: opacity 0.4s ease;
}

.letter.visible {
  opacity: 1;
}

/* ─── A group ─── */
/*
  A is in raw SVG coords (not inside the 1.975x letters group).
  A centre x ≈ 22.3, full logo centre x ≈ 165.5
  → translateX(143) centres A on the logo.
  → translateX(0)   is A's natural settled position (left of the S with a small gap).
  Y baselines already match (both reach y≈57).
*/

.letter-a-group {
  opacity: 0;
  transform: translateX(0);
  transition:
      opacity 0.5s ease,
      transform 0.55s cubic-bezier(0.22, 1, 0.36, 1);
  will-change: transform, opacity;
}

.letter-a-group.a-hidden {
  opacity: 0;
  transform: translateX(0);
}

/* Fade in at centred position — no motion transition yet */
.letter-a-group.a-enter {
  opacity: 1;
  transform: translateX(143px);
  transition:
      opacity 0.5s ease,
      transform 0s;
}

/* Zip left to natural position */
.letter-a-group.a-settled {
  opacity: 1;
  transform: translateX(0);
  transition:
      opacity 0.3s ease,
      transform 0.55s cubic-bezier(0.22, 1, 0.36, 1);
}

/* Zip right back to centre then fade out */
.letter-a-group.a-exit {
  animation: a-zip-out 0.7s cubic-bezier(0.22, 1, 0.36, 1) both;
}

@keyframes a-zip-out {
  0%   { opacity: 1; transform: translateX(0); }
  55%  { opacity: 1; transform: translateX(143px); }
  100% { opacity: 0; transform: translateX(143px); }
}
</style>